import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * Reusable hero media layer: autoplay-muted Vimeo `background` video over a
 * poster image.  Poster paints first; the video fades in only once Vimeo
 * reports frames are actually playing (Player.js postMessage bridge).
 *
 * Optional `loopStart` / `loopEnd` (seconds) constrain playback to a window.
 * The layer briefly dissolves to the poster around the seam so the seek is
 * never visible as a hard cut.
 *
 * variant="bleed" — absolute layer bleeding off its parent's edges.
 * variant="card"  — self-contained rounded aspect card.
 */
const extractVimeo = (url) => {
  if (!url) return { id: null, hash: null };
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-f0-9]+))?/);
  const q = url.match(/[?&]h=([a-f0-9]+)/);
  return { id: m ? m[1] : null, hash: (m && m[2]) || (q && q[1]) || null };
};

const SEAM_LEAD = 1.0; // seconds before loopEnd to start dissolving out
const post = (win, method, value) =>
  win.postMessage(JSON.stringify(value === undefined ? { method } : { method, value }), '*');

const HeroMediaLayer = forwardRef(({
  posterImage,
  videoUrl,
  alt = '',
  objectPosition = 'right center',
  variant = 'bleed',
  className = '',
  posterClassName = '',
  testid = 'hero-media',
  loopStart,
  loopEnd,
  children,
}, ref) => {
  const [videoReady, setVideoReady] = useState(false);
  const [videoAllowed, setVideoAllowed] = useState(false);
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const [cover, setCover] = useState(null);
  const seekingRef = useRef(false);
  const readyRef = useRef(false);
  const { id: vimeoId, hash: vimeoHash } = extractVimeo(videoUrl);
  const hasLoopWindow = typeof loopStart === 'number' && typeof loopEnd === 'number' && loopEnd > loopStart;

  const fadeTimer = useRef(null);
  useImperativeHandle(ref, () => ({
    setMuted: (muted) => {
      clearInterval(fadeTimer.current);
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!win) return;
      post(win, 'setMuted', muted);
      post(win, 'setVolume', muted ? 0 : 1);
    },
    fadeToMute: (ms = 700) => {
      clearInterval(fadeTimer.current);
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!win) return;
      const steps = 10;
      let i = steps;
      fadeTimer.current = setInterval(() => {
        i -= 1;
        post(win, 'setVolume', Math.max(0, i / steps));
        if (i <= 0) {
          clearInterval(fadeTimer.current);
          post(win, 'setMuted', true);
        }
      }, ms / steps);
    },
  }));
  useEffect(() => () => clearInterval(fadeTimer.current), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!vimeoId) {
      setVideoAllowed(false);
      setVideoReady(false);
      readyRef.current = false;
      return;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    const raf = requestAnimationFrame(() => setVideoAllowed(true));
    return () => cancelAnimationFrame(raf);
  }, [vimeoId]);

  useEffect(() => {
    if (!videoAllowed) return;
    const handleMessage = (e) => {
      if (typeof e.data !== 'string' || !e.data.includes('vimeo')) return;
      let payload;
      try { payload = JSON.parse(e.data); } catch { return; }
      const win = iframeRef.current && iframeRef.current.contentWindow;
      if (!win || e.source !== win) return;

      if (payload.event === 'ready') {
        readyRef.current = true;
        post(win, 'addEventListener', 'playing');
        post(win, 'addEventListener', 'timeupdate');
        if (hasLoopWindow) {
          post(win, 'addEventListener', 'seeked');
          seekingRef.current = true;
          post(win, 'setCurrentTime', loopStart);
        }
      }
      if (payload.event === 'playing' && !hasLoopWindow) setVideoReady(true);
      if (payload.event === 'seeked' && hasLoopWindow) {
        seekingRef.current = false;
        setVideoReady(true);
      }
      if (payload.event === 'timeupdate' && !hasLoopWindow) {
        const d = payload.data || {};
        if (typeof d.seconds !== 'number' || typeof d.duration !== 'number') return;
        if (d.duration - d.seconds < 0.9) setVideoReady(false);
        else if (d.seconds < 1.5) setVideoReady(true);
      }
      if (payload.event === 'timeupdate' && hasLoopWindow && !seekingRef.current) {
        const t = payload.data && payload.data.seconds;
        if (typeof t !== 'number') return;
        if (t >= loopEnd - SEAM_LEAD) setVideoReady(false);
        if (t >= loopEnd || t < loopStart - 1) {
          seekingRef.current = true;
          post(win, 'setCurrentTime', loopStart);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    const fallback = setTimeout(() => { if (readyRef.current) setVideoReady(true); }, 8000);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(fallback);
    };
  }, [videoAllowed, hasLoopWindow, loopStart, loopEnd]);

  useEffect(() => {
    if (!videoAllowed || !wrapRef.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const win = iframeRef.current && iframeRef.current.contentWindow;
        if (!win || !readyRef.current) return;
        post(win, entry.isIntersecting ? 'play' : 'pause');
      },
      { threshold: 0 }
    );
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, [videoAllowed]);

  useEffect(() => {
    if (variant !== 'bleed' || !wrapRef.current || typeof ResizeObserver === 'undefined') return;
    const el = wrapRef.current;
    const measure = () => {
      const { width: w, height: h } = el.getBoundingClientRect();
      if (!w || !h) return;
      const ratio = 16 / 9;
      setCover(w / h > ratio ? { width: w, height: w / ratio } : { width: h * ratio, height: h });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variant]);

  const alignRight = objectPosition.includes('right');
  const alignLeft = objectPosition.includes('left');
  const coverStyle = cover
    ? {
        width: cover.width,
        height: cover.height,
        top: '50%',
        transform: alignRight || alignLeft ? 'translateY(-50%)' : 'translate(-50%, -50%)',
        ...(alignRight ? { right: 0 } : alignLeft ? { left: 0 } : { left: '50%' }),
      }
    : null;

  const iframe = videoAllowed && vimeoId && (
    <iframe
      ref={iframeRef}
      src={`https://player.vimeo.com/video/${vimeoId}?${vimeoHash ? `h=${vimeoHash}&` : ''}background=1&autoplay=1&muted=1&loop=1&byline=0&title=0&portrait=0&autopause=0&player_id=hero-vimeo-${vimeoId}`}
      title={alt || 'Hero background video'}
      aria-hidden="true"
      tabIndex={-1}
      allow="autoplay; fullscreen; picture-in-picture"
      data-testid={`${testid}-video`}
      className={`absolute pointer-events-none border-0 transition-opacity duration-1000 ease-in-out ${
        coverStyle ? '' : 'inset-0 w-full h-full'
      } ${videoReady ? 'opacity-100' : 'opacity-0'}`}
      style={{ filter: 'contrast(1.06) saturate(1.06) brightness(1.02)', ...(coverStyle || {}) }}
    />
  );

  if (variant === 'card') {
    return (
      <div
        ref={wrapRef}
        className={`relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] bg-slate-900/40 ${className}`}
        data-testid={testid}
      >
        {posterImage && (
          <img
            src={posterImage}
            alt={alt}
            className={`w-full h-full object-cover ${posterClassName}`}
            style={{ objectPosition, filter: 'contrast(1.08) saturate(1.06) brightness(1.02)' }}
            loading="eager"
            fetchpriority="high"
          />
        )}
        {iframe}
        {children}
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      data-testid={testid}
    >
      {posterImage && (
        <img
          src={posterImage}
          alt=""
          aria-hidden="true"
          className={`w-full h-full object-cover ${posterClassName}`}
          style={{ objectPosition, filter: 'contrast(1.08) saturate(1.06) brightness(1.02)' }}
          loading="eager"
          fetchpriority="high"
        />
      )}
      {iframe}
      {children}
    </div>
  );
});

HeroMediaLayer.displayName = 'HeroMediaLayer';

export default HeroMediaLayer;
